import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, ButtonBase, Collapse, Tooltip, Typography } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useLocalStorageState } from "../../../../hooks/useLocalStorageState";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import { ShiftTypeIcon } from "../../../MasterSchedule/v2/auxillary/ShiftTypeIcon";
import type { GridDates, HiringGapsGrid, HiringGridCoord, HiringMetricKey, MetricRowKey } from "../types";
import { type GapTone, gapToneStyle } from "../utils/gapTones";
import { displayedDateSpan, formatWeekHeader } from "../utils/gridDates";
import {
	buildMissingGrid,
	buildMissingToneFns,
	cellMissing,
	cellTerms,
	formatMissing,
	gapsRowTypes,
	gridVal,
	type HiringGridBundle,
} from "../utils/missingGrid";
import {
	activeShiftFilter,
	DEFAULT_DETAILED_VIEW,
	DEFAULT_GAPS_OPEN,
	DEFAULT_SHOW_FLAGS,
	gapsDetailedViewStorageKey,
	gapsOpenStorageKey,
	gapsShowFlagsStorageKey,
	type HiringStorageScope,
} from "../utils/preferences";
import { shiftLabelStartMinutes, shiftLabelToShiftType } from "../utils/shiftLabel";
import { AMBER, BLUE, EMERALD, PURPLE, RADIUS, ROSE, SLATE } from "../utils/styleTokens";
import { cellKey } from "../utils/suggestedHires";
import { columnToDayIdx, dayKeyAt } from "../utils/weekStart";
import AbsencesMetricHeader from "./AbsencesMetricHeader";
import { ShiftScopeChips } from "./ShiftScopeChips";

// Display order + color of the actual-data breakdown rows (between Needed and Recurring). `countable`
// rows have a Count toggle and feed the Missing gap; Agency is display-only (DAT-248).
const METRIC_ROWS: MetricRow[] = [
	{ key: "structural_ot", labelKey: "hiring.gaps.structuralOt", color: ROSE[600], bg: `${ROSE[50]}99`, countable: true },
	{ key: "internal_pickup_ot", labelKey: "hiring.gaps.internalPickupOt", color: AMBER[700], bg: `${AMBER[50]}99`, countable: true },
	{ key: "internal_pickup_non_ot", labelKey: "hiring.gaps.internalPickupNonOt", color: EMERALD[600], bg: `${EMERALD[50]}99`, countable: true },
	{ key: "agency", labelKey: "hiring.gaps.agency", color: BLUE[600], bg: `${BLUE[50]}99`, countable: false },
	{ key: "absences", labelKey: "hiring.gaps.absences", color: PURPLE[600], bg: `${PURPLE[50]}99`, countable: true },
];

type Props = {
	/** One date per grid day, in week-start-aligned column order. Indexed only through `utils/gridDates`. */
	dates: GridDates;
	/**
	 * The department's own timezone, for deciding which week is the ACTIVE one. Passed in rather than
	 * read from a context so this stays a presentational component that is told what it is drawing —
	 * and so "today" is the department's day, not the viewer's.
	 */
	timezone: string;
	neededGrid: HiringGapsGrid;
	actualGrid: HiringGapsGrid;
	structuralOtGrid: HiringGapsGrid;
	internalPickupOtGrid: HiringGapsGrid;
	internalPickupNonOtGrid: HiringGapsGrid;
	agencyGrid: HiringGapsGrid;
	absencesGrid: HiringGapsGrid;
	// Applied suggested hires, in the same shape as `actualGrid`. A CLIENT-SIDE overlay added to Recurring
	// at render time: it feeds Missing, the cell tones and the `X + Y` split, and never touches the
	// department's real schedule template — this module is read-only.
	appliedGrid?: HiringGapsGrid;
	templateHppd: Record<number, number>;
	highlightedCoords: HiringGridCoord[];
	// Pinned selection — outlives the pointer, unlike `highlightedCoords`. Both may be set at once
	// (hovering one hire while another stays pinned), and pinned wears the stronger treatment.
	pinnedCoords?: HiringGridCoord[];
	weekStartDay: number;
	gridWeeks: number;
	weekNumbers: number[];
	onActualCellClick: (coord: HiringGridCoord) => void;
	// Count levers are owned by HiringModule (they drive the server-recomputed recommendations too).
	// Keyed by HiringMetricKey — agency is display-only and is NOT a count lever.
	includeFlags: Record<HiringMetricKey, boolean>;
	onToggleMetric: (key: HiringMetricKey) => void;
	// Which shift types to draw rows for. The filter itself is ONE control and it lives in the Suggested
	// Hires Settings popover — this table used to carry a second copy of it, which was one page asking
	// one question twice. Stored labels are the SELECTED ones and an empty list means "no filter", so a
	// shift type added to the master schedule later is drawn by default.
	shiftFilterLabels: string[];
	onToggleShift: (label: string) => void;
	// Who this grid is being rendered for — the table owns its remaining view preferences (open, detailed
	// view, which breakdown rows show) and persists them per user, so it needs the scope those keys are
	// built from. Passed in rather than read from a context: this stays a presentational component that
	// is TOLD whose grid it is drawing.
	storageScope: HiringStorageScope;
};

// Metric breakdown rows shown in Detailed View. Each pairs an actual-data grid with a tone so the
// row reads at a glance. Order here is the display order between Needed and Recurring. `MetricRowKey`
// (wider than `HiringMetricKey` — `agency` is a row but not a count lever) lives in `types.ts` so the
// persisted "which rows show" preference can be typed there too.
type MetricRow = {
	key: MetricRowKey;
	labelKey: string;
	color: string;
	bg: string;
	// Display-only rows (agency) have no Count toggle and never feed the Missing gap.
	countable: boolean;
};

type CellTone = { bg?: string; color: string; bar?: string };

const toneStyleToCellTone = (tone: GapTone): CellTone => {
	const style = gapToneStyle(tone);
	return { color: style.ink, bg: style.bg, bar: style.bar };
};

type HoveredCell = { weekIdx: number; dayIdx: number; shiftLabel: string; rowType: string };

const RecurringGapsTable: FC<Props> = ({
	dates,
	timezone,
	neededGrid,
	actualGrid,
	structuralOtGrid,
	internalPickupOtGrid,
	internalPickupNonOtGrid,
	agencyGrid,
	absencesGrid,
	appliedGrid,
	templateHppd,
	highlightedCoords,
	pinnedCoords,
	weekStartDay,
	gridWeeks,
	weekNumbers,
	onActualCellClick,
	includeFlags,
	onToggleMetric,
	shiftFilterLabels,
	onToggleShift,
	storageScope,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { userId, departmentId } = storageScope;
	// Every view choice below is a PREFERENCE, so it survives a reload — per user + department, because
	// one manager's reading of the grid must not change another's (PRD). `hovered` is the exception on
	// purpose: it is where the pointer is, which nobody wants restored.
	const [open, setOpen] = useLocalStorageState<boolean>(gapsOpenStorageKey(userId, departmentId), DEFAULT_GAPS_OPEN);
	const [hovered, setHovered] = useState<HoveredCell | null>(null);
	// Default view is collapsed: a single "Missing" row per shift. Detailed View expands it into the
	// Needed / actual-data breakdown / Recurring / Missing stack.
	const [detailedView, setDetailedView] = useLocalStorageState<boolean>(gapsDetailedViewStorageKey(userId, departmentId), DEFAULT_DETAILED_VIEW);

	// "Present" vs "calculate" are split into two independent controls per metric:
	//   • showFlags (presentation only) — which breakdown rows render in Detailed View.
	//   • includeFlags (Count; owned by HiringModule) — which metrics count toward Missing AND drive the
	//     server-recomputed recommendations via `includedMetrics`. Decoupled from detailedView on purpose.
	const [showFlags, setShowFlags] = useLocalStorageState<Record<MetricRowKey, boolean>>(gapsShowFlagsStorageKey(userId, departmentId), DEFAULT_SHOW_FLAGS);
	const toggleShow = (key: MetricRowKey) =>
		setShowFlags((prev) => {
			track("hiring_gaps_toggle_changed", { toggle: `${key}_show`, enabled: !prev[key] });
			return { ...prev, [key]: !prev[key] };
		});

	const gridByKey: Record<MetricRowKey, HiringGapsGrid> = {
		structural_ot: structuralOtGrid,
		internal_pickup_ot: internalPickupOtGrid,
		internal_pickup_non_ot: internalPickupNonOtGrid,
		agency: agencyGrid,
		absences: absencesGrid,
	};
	const activeMetricRows = useMemo(() => (detailedView ? METRIC_ROWS.filter((m) => showFlags[m.key]) : []), [detailedView, showFlags]);

	// Applied suggested hires join the template's own coverage as a render-time overlay: the head nurse is
	// telling us these people exist (already hired, still in orientation, or about to be), so Recurring
	// reads `X + Y` and every number derived from it closes by that much.
	const grids: HiringGridBundle = useMemo(
		() => ({ neededGrid, actualGrid, appliedGrid, structuralOtGrid, internalPickupOtGrid, internalPickupNonOtGrid, absencesGrid }),
		[neededGrid, actualGrid, appliedGrid, structuralOtGrid, internalPickupOtGrid, internalPickupNonOtGrid, absencesGrid],
	);

	// The Missing formula lives in `utils/missingGrid` — the ONE frontend copy, shared with the still-short
	// markers on the Suggested Hires day chips. Recomputed here on every toggle with no refetch.
	const computeMissing = useCallback(
		(label: string, weekIdx: number, dayIdx: number): number => cellMissing(grids, includeFlags, label, cellKey(weekIdx, dayIdx)),
		[grids, includeFlags],
	);

	const toggleDetailedView = () =>
		setDetailedView((v) => {
			track("hiring_gaps_toggle_changed", { toggle: "detailed_view", enabled: !v });
			return !v;
		});

	const columns = useMemo(() => Array.from({ length: 7 }, (_, c) => c), []);

	const allShiftLabels = useMemo(() => {
		const labels = new Set<string>([...Object.keys(neededGrid), ...Object.keys(actualGrid)]);
		return [...labels].sort((a, b) => shiftLabelStartMinutes(a) - shiftLabelStartMinutes(b));
	}, [neededGrid, actualGrid]);

	/**
	 * The filter in force. Derived here rather than straight off the stored array because a stored label
	 * can outlive the template it names (a renamed or removed shift type): filtering on a label that no
	 * longer exists would empty the grid for good, and the chips that could clear it render inside the
	 * grid it just emptied. `activeShiftFilter` drops those, so a vanished label stops filtering.
	 */
	const shiftFilter = useMemo(() => activeShiftFilter(shiftFilterLabels, allShiftLabels), [shiftFilterLabels, allShiftLabels]);

	const shiftLabels = useMemo(() => {
		if (shiftFilter.size === 0) return allShiftLabels;
		return allShiftLabels.filter((label) => shiftFilter.has(label));
	}, [allShiftLabels, shiftFilter]);

	const highlightSet = useMemo(() => {
		const set = new Set<string>();
		for (const c of highlightedCoords) {
			set.add(`${c.shiftLabel}-${c.weekIdx}-${c.dayIdx}`);
		}
		return set;
	}, [highlightedCoords]);

	const pinnedSet = useMemo(() => {
		const set = new Set<string>();
		for (const c of pinnedCoords ?? []) {
			set.add(`${c.shiftLabel}-${c.weekIdx}-${c.dayIdx}`);
		}
		return set;
	}, [pinnedCoords]);

	// One tone function per shift label, so a shift is ranked against its OWN worst days — a quiet
	// 11p-7a doesn't get graded by an 8a-4p outlier. Built by the same helpers the Suggested Hires
	// day-chip markers use, so the two can never disagree about which days are short.
	const missingToneFns = useMemo(() => buildMissingToneFns(buildMissingGrid(grids, includeFlags, gridWeeks)), [grids, includeFlags, gridWeeks]);

	const weekIndices = useMemo(() => Array.from({ length: gridWeeks }, (_, i) => i), [gridWeeks]);

	const wrapperSx = {
		mb: 4,
		borderRadius: RADIUS["2xl"],
		bgcolor: "white",
		border: `1px solid ${SLATE[200]}`,
		boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
		overflow: "hidden",
	};

	if (shiftLabels.length === 0) {
		return (
			<Box data-testid="hiring-gaps-table-empty" sx={{ ...wrapperSx, p: 3 }}>
				<Typography sx={{ fontSize: 14, color: SLATE[500] }}>{t("hiring.gaps.empty")}</Typography>
			</Box>
		);
	}

	const cornerHeadSx = {
		p: 1.5,
		fontSize: 12,
		fontWeight: 600,
		color: SLATE[500],
		bgcolor: SLATE[50],
		textAlign: "left" as const,
	};
	const dayHeadSx = {
		p: 1.5,
		fontSize: 12,
		fontWeight: 600,
		color: SLATE[500],
		bgcolor: SLATE[50],
		textAlign: "center" as const,
		minWidth: 56,
		borderLeft: "1px solid white",
	};

	// One term of the Missing arithmetic, for the per-cell breakdown tooltip.
	type MissingTerm = { label: string; value: number; sign: "+" | "−"; counted: boolean };

	// Every term of this cell's Missing, in the order the formula applies them — including the ones the
	// Count levers left OUT, named as excluded. The durable answer to "why doesn't this number add up":
	// which rows are in the total was previously undiscoverable.
	const missingBreakdown = (label: string, weekIdx: number, dayIdx: number): MissingTerm[] => {
		const key = cellKey(weekIdx, dayIdx);
		const { needed, template, applied } = cellTerms(grids, includeFlags, label, key);
		const terms: MissingTerm[] = [
			{ label: t("hiring.gaps.neededShort"), value: needed, sign: "+", counted: true },
			{ label: t("hiring.gaps.recurring"), value: template, sign: "−", counted: true },
		];
		if (applied > 0) {
			terms.push({ label: t("hiring.gaps.appliedHires"), value: applied, sign: "−", counted: true });
		}
		for (const metric of METRIC_ROWS) {
			if (!metric.countable) {
				continue;
			}
			const value = gridVal(gridByKey[metric.key], label, key);
			if (value === 0) {
				continue;
			}
			// Structural OT and absences ADD to the gap (band-aid coverage / phantom coverage);
			// real internal pickups SUBTRACT.
			const sign = metric.key === "structural_ot" || metric.key === "absences" ? "+" : "−";
			terms.push({ label: t(metric.labelKey), value, sign, counted: includeFlags[metric.key as HiringMetricKey] });
		}
		return terms;
	};

	const missingTooltip = (label: string, weekIdx: number, dayIdx: number, missing: number): ReactNode => {
		const terms = missingBreakdown(label, weekIdx, dayIdx);
		const excluded = terms.filter((term) => !term.counted).map((term) => term.label);
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, fontSize: 11 }}>
				{terms.map((term) => (
					<Box
						key={term.label}
						sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, ...(term.counted ? {} : { opacity: 0.55, textDecoration: "line-through" }) }}
					>
						<Box component="span">{term.label}</Box>
						<Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{`${term.sign}${term.value}`}</Box>
					</Box>
				))}
				<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, mt: 0.25, pt: 0.25, borderTop: `1px solid ${SLATE[500]}`, fontWeight: 700 }}>
					<Box component="span">{t("hiring.gaps.missing")}</Box>
					<Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
						{formatMissing(missing)}
					</Box>
				</Box>
				{excluded.length > 0 && (
					<Box component="span" sx={{ mt: 0.5, opacity: 0.75 }}>
						{t("hiring.gaps.cellTooltip.excluded", { rows: excluded.join(", ") })}
					</Box>
				)}
			</Box>
		);
	};

	// Resolve a cell's displayed content, tone, click-ability, and font weight for any row type:
	// needed (detailed view), recurring/missing/<metric>.
	const resolveCell = (rowType: string, label: string, weekIdx: number, dayIdx: number) => {
		const key = cellKey(weekIdx, dayIdx);
		const needed = gridVal(neededGrid, label, key);
		const template = gridVal(actualGrid, label, key);
		const applied = gridVal(appliedGrid, label, key);
		const metricRow = METRIC_ROWS.find((m) => m.key === rowType);

		if (rowType === "needed") {
			return { content: needed || "", tone: { color: SLATE[400] } as CellTone, clickable: false, bold: false, tooltip: undefined as ReactNode };
		}
		if (rowType === "recurring") {
			// Recurring stays neutral (the Missing row carries the color). Where applied hires exist the
			// cell reads `X + Y`: X from the schedule template, Y the hires the user applied.
			const content: ReactNode =
				applied > 0 ? (
					<Box component="span">
						{template}
						<Box component="span" sx={{ color: EMERALD[600], fontWeight: 800 }}>{` +${applied}`}</Box>
					</Box>
				) : (
					template || ""
				);
			const tooltip = applied > 0 ? t("hiring.gaps.recurringSplitTooltip", { template, applied }) : undefined;
			return { content, tone: { color: SLATE[500] } as CellTone, clickable: template > 0, bold: true, tooltip };
		}
		if (rowType === "missing") {
			const missing = computeMissing(label, weekIdx, dayIdx);
			const toneFn = missingToneFns[label];
			const tone: CellTone = toneStyleToCellTone(toneFn ? toneFn(missing) : "neutral");
			// Signed, so the direction is on the cell instead of only in its colour: `-N` short, `+N` over,
			// and a check when the day is exactly covered (the old code printed a bare "0" for BOTH, which
			// is what made an overstaffed cell look like an unexplained warning).
			const content: ReactNode =
				missing === 0 ? <CheckIcon sx={{ fontSize: 14, verticalAlign: "middle" }} aria-label={t("hiring.gaps.balanced")} /> : formatMissing(missing);
			// Clicking opens the staffed-nurses drilldown when the slot has real (template) coverage.
			return { content, tone, clickable: template > 0, bold: true, tooltip: missingTooltip(label, weekIdx, dayIdx, missing) };
		}
		if (metricRow) {
			const v = gridVal(gridByKey[metricRow.key], label, key);
			const tone: CellTone = v > 0 ? { color: metricRow.color, bg: metricRow.bg } : { color: SLATE[300] };
			return { content: v > 0 ? String(v) : "–", tone, clickable: false, bold: false, tooltip: undefined as ReactNode };
		}
		return { content: "", tone: { color: SLATE[600] } as CellTone, clickable: false, bold: false, tooltip: undefined as ReactNode };
	};

	const renderMetricCells = (label: string, rowType: string) => {
		return weekIndices.flatMap((weekIdx) =>
			columns.map((column) => {
				const dayIdx = columnToDayIdx(column, weekStartDay);
				const dayKey = dayKeyAt(column, weekStartDay);
				const { content, tone, clickable: isClickable, bold, tooltip } = resolveCell(rowType, label, weekIdx, dayIdx);
				const coordKey = `${label}-${weekIdx}-${dayIdx}`;
				/**
				 * The MISSING cell only — not the whole column.
				 *
				 * Both highlights arrive as `{shiftLabel, weekIdx, dayIdx}` from something that is talking
				 * about a GAP: a suggested hire's day chip, or a Move card's from/to slot. In Detailed View
				 * that coordinate matches a cell in every breakdown row, so ringing all of them boxed the
				 * whole column and pointed at seven numbers when the claim was about one.
				 */
				const isGapCell = rowType === "missing";
				const isPinned = isGapCell && pinnedSet.has(coordKey);
				const isHighlighted = isGapCell && !isPinned && highlightSet.has(coordKey);
				const handleClick = isClickable ? () => onActualCellClick({ weekIdx, dayIdx, shiftLabel: label }) : undefined;
				const cell = (
					<Box
						component="td"
						key={`${label}-${weekIdx}-${rowType}-${dayKey}`}
						data-testid={`hiring-cell-${label}-${weekIdx}-${dayIdx}-${rowType}`}
						onMouseEnter={() => setHovered({ weekIdx, dayIdx, shiftLabel: label, rowType })}
						onMouseLeave={() => setHovered(null)}
						{...(isClickable
							? {
									role: "button",
									tabIndex: 0,
									onClick: handleClick,
									onKeyDown: (e: React.KeyboardEvent) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											handleClick?.();
										}
									},
								}
							: {})}
						sx={{
							position: "relative",
							p: 1.5,
							textAlign: "center",
							fontWeight: bold ? 700 : 500,
							color: tone.color,
							bgcolor: tone.bg ?? "transparent",
							borderLeft: `1px solid ${SLATE[50]}`,
							transition: "all 200ms ease",
							// A 3px rule on the leading edge of a short cell: a column of gaps then reads as a
							// continuous edge the eye catches in peripheral vision, not isolated swatches.
							...(tone.bar ? { boxShadow: `inset 3px 0 0 0 ${tone.bar}` } : {}),
							...(isClickable
								? {
										cursor: "pointer",
										"&:hover": { bgcolor: tone.bg ?? SLATE[100] },
										"&:focus-visible": { outline: `2px solid ${BLUE[500]}`, outlineOffset: -2 },
									}
								: {}),
							...(isHighlighted ? { boxShadow: `inset 0 0 0 2px ${BLUE[500]}`, bgcolor: `${BLUE[50]}80` } : {}),
							// Pinned outranks hover: a clicked hire's cells stay marked while the pointer moves on.
							...(isPinned ? { boxShadow: `inset 0 0 0 2px ${BLUE[700]}`, bgcolor: `${BLUE[100]}b3` } : {}),
						}}
					>
						{content}
					</Box>
				);
				return tooltip ? (
					<Tooltip key={`${label}-${weekIdx}-${rowType}-${dayKey}`} title={tooltip} placement="top" arrow enterDelay={300}>
						{cell}
					</Tooltip>
				) : (
					cell
				);
			}),
		);
	};

	// See `gapsRowTypes` — extracted so the row-type logic is testable on its own.
	const shiftRowTypes: string[] = gapsRowTypes(
		detailedView,
		activeMetricRows.map((m) => m.key),
	);

	const rowLabel = (rowType: string): string => {
		const metricRow = METRIC_ROWS.find((m) => m.key === rowType);
		if (metricRow) return t(metricRow.labelKey);
		if (rowType === "needed") return t("hiring.gaps.neededShort");
		return t(`hiring.gaps.${rowType}`); // recurring | missing
	};

	const rowLabelColor = (rowType: string, active: boolean): string => {
		const metricRow = METRIC_ROWS.find((m) => m.key === rowType);
		if (metricRow) return metricRow.color;
		if (rowType === "missing") return active ? BLUE[800] : SLATE[600];
		return active ? BLUE[800] : SLATE[400];
	};

	// One metric's caption over its Show / Count switches, in the toggle bar.
	const renderMetricGroup = (metric: MetricRow): ReactElement => (
		<Box sx={{ display: "inline-flex", flexDirection: "column", gap: 0.5 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 700, color: metric.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>
				{t(metric.labelKey)}
			</Typography>
			<Box sx={{ display: "inline-flex", gap: 1.5 }}>
				{/* Show = presentation only (which breakdown row renders in Detailed View). */}
				<Box sx={{ display: "inline-flex", opacity: detailedView ? 1 : 0.4 }}>
					<SystemSwitch
						checked={showFlags[metric.key]}
						onChange={() => toggleShow(metric.key)}
						disabled={!detailedView}
						label={t("hiring.gaps.show")}
						dataTestId={`hiring-gaps-${metric.key}-show-toggle`}
					/>
				</Box>
				{/* Count = include in Missing + recommendations. Independent of Detailed View. Display-only
			    rows (agency) have no Count toggle. */}
				{metric.countable && (
					<SystemSwitch
						checked={includeFlags[metric.key as HiringMetricKey]}
						onChange={() => onToggleMetric(metric.key as HiringMetricKey)}
						label={t("hiring.gaps.count")}
						dataTestId={`hiring-gaps-${metric.key}-toggle`}
					/>
				)}
			</Box>
		</Box>
	);

	return (
		<Box data-testid="hiring-gaps-table" sx={wrapperSx}>
			<ButtonBase
				onClick={() => setOpen((v) => !v)}
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					p: 2.5,
					"&:hover": { bgcolor: SLATE[50] },
					transition: "background 150ms ease",
				}}
				data-testid="hiring-gaps-toggle"
			>
				<Typography sx={{ fontSize: 16, fontWeight: 700, color: SLATE[900] }}>{t("hiring.gaps.title")}</Typography>
				<KeyboardArrowDownIcon
					sx={{
						fontSize: 20,
						color: SLATE[400],
						transition: "transform 200ms ease",
						transform: open ? "rotate(180deg)" : "rotate(0deg)",
					}}
				/>
			</ButtonBase>
			<Collapse in={open} unmountOnExit>
				<Box
					sx={{
						display: "flex",
						// BOTTOM-aligned, not centred: the five metric groups are two-line cells (a coloured
						// caption over a Show/Count switch pair) and Detailed View is a bare switch, so centring
						// resolved its centre against the whole two-line box and dropped it between the caption
						// row and the switch row. Aligning the line's items to their bottom edge puts every
						// switch in the bar — all the same height — on one baseline, with the captions above
						// them. Chosen over giving Detailed View an empty caption slot: that would add an element
						// whose only job is to occupy space, and it would also make the master toggle look like
						// a sixth metric group. Bottom-alignment applies per flex LINE, so the bar still wraps
						// at narrow widths and a caption that wraps to two lines just makes its own cell taller.
						alignItems: "flex-end",
						justifyContent: "flex-end",
						flexWrap: "wrap",
						gap: { xs: 1.5, sm: 3 },
						px: 2.5,
						py: 1.5,
						borderTop: `1px solid ${SLATE[100]}`,
						bgcolor: "white",
					}}
					data-testid="hiring-gaps-toggle-bar"
				>
					{/* A row of its own, so Detailed View and the metric switches always start together on the next line
					    instead of sharing whatever room a short list of shifts leaves. */}
					{allShiftLabels.length > 1 && (
						<Box sx={{ flexBasis: "100%" }}>
							<ShiftScopeChips shiftLabels={allShiftLabels} enabledShiftLabels={shiftLabels} onToggleShift={onToggleShift} />
						</Box>
					)}
					<SystemSwitch checked={detailedView} onChange={toggleDetailedView} label={t("hiring.gaps.detailedView")} dataTestId="hiring-gaps-detailed-toggle" />
					{METRIC_ROWS.map((metric) => {
						const group = renderMetricGroup(metric);
						// Absences also says how many items it counts and opens the checklist that decides them.
						return metric.key === "absences" ? (
							<AbsencesMetricHeader key={metric.key}>{group}</AbsencesMetricHeader>
						) : (
							<Tooltip key={metric.key} title={t(`${metric.labelKey}Tooltip`)} placement="top" arrow>
								{group}
							</Tooltip>
						);
					})}
				</Box>
				<Box sx={{ overflowX: "auto", borderTop: `1px solid ${SLATE[100]}` }}>
					<Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
						<Box component="thead">
							<Box component="tr" sx={{ bgcolor: SLATE[800], color: "white" }}>
								<Box component="th" sx={{ p: 1.5, textAlign: "left", width: 96, fontWeight: 600 }} colSpan={2}>
									{" "}
								</Box>
								{weekIndices.map((weekIdx) => {
									const isHovered = hovered?.weekIdx === weekIdx;
									return (
										<Box
											key={`week-header-${weekIdx}`}
											component="th"
											colSpan={7}
											sx={{
												p: 1.5,
												textAlign: "center",
												borderLeft: `1px solid ${SLATE[700]}`,
												fontWeight: 600,
												bgcolor: isHovered ? BLUE[700] : "transparent",
												transition: "background 150ms ease",
											}}
										>
											{/* The Master Schedule's own caption, verbatim: `Week 1 (active 21–27 Sep)`. A manager
											    reading this grid is deciding about a real fortnight, and the week number alone made
											    them cross-reference the schedule to find out which one. */}
											{/* Indexed bare, like the Add Hire dialog and `formatCoordLabel`: `weekNumbers` is
											    `list(range(1, grid_weeks + 1))` from the backend and parallel to the weeks this
											    loop draws, so a `?? weekIdx + 1` fallback could only ever re-derive the value it
											    already has — while hiding a payload that had stopped being parallel. */}
											{formatWeekHeader(dates, weekIdx, weekNumbers[weekIdx], timezone, t)}
										</Box>
									);
								})}
							</Box>
							<Box component="tr">
								<Box component="th" sx={{ ...cornerHeadSx, width: 96 }}>
									{t("hiring.gaps.shift")}
								</Box>
								<Box component="th" sx={{ ...cornerHeadSx, width: 80 }}>
									{t("hiring.gaps.row")}
								</Box>
								{weekIndices.flatMap((weekIdx) =>
									columns.map((column) => {
										const dayKey = dayKeyAt(column, weekStartDay);
										const dayIdx = columnToDayIdx(column, weekStartDay);
										const isHovered = hovered?.weekIdx === weekIdx && hovered?.dayIdx === dayIdx;
										return (
											<Box
												component="th"
												key={`hdr-${weekIdx}-${dayKey}`}
												sx={{
													...dayHeadSx,
													bgcolor: isHovered ? BLUE[100] : dayHeadSx.bgcolor,
													color: isHovered ? BLUE[800] : dayHeadSx.color,
													transition: "background 150ms ease",
												}}
											>
												{t(`hiring.day.${dayKey}`)}
											</Box>
										);
									}),
								)}
							</Box>
						</Box>
						<Box component="tbody">
							{shiftLabels.map((label, labelIdx) => {
								const isShiftHovered = hovered?.shiftLabel === label;
								return shiftRowTypes.map((rowType, rowIdx) => {
									const isFirstRow = rowIdx === 0;
									const isRowHovered = isShiftHovered && hovered?.rowType === rowType;
									// First row of each shift block (except the very first) gets a heavier rule so shifts
									// read as distinct blocks; rows within a shift keep the light hairline.
									const topBorder =
										labelIdx === 0 && isFirstRow ? {} : isFirstRow ? { borderTop: `2px solid ${SLATE[200]}` } : { borderTop: `1px solid ${SLATE[50]}` };
									const isMissingRow = rowType === "missing";
									// A *countable* metric row shown without being counted reads dimmed, so it's clear it isn't
									// in Missing. Agency (display-only) is never counted, so it's not treated as "uncounted".
									const isUncountedMetric = METRIC_ROWS.some((m) => m.key === rowType && m.countable) && !includeFlags[rowType as HiringMetricKey];
									return (
										<Box
											component="tr"
											key={`${label}-${rowType}`}
											data-testid={`hiring-row-${label}-0-${rowType}`}
											sx={{
												...topBorder,
												...(isUncountedMetric ? { opacity: 0.5 } : {}),
												...(isMissingRow ? { bgcolor: SLATE[50], borderTop: `2px solid ${SLATE[200]}` } : {}),
											}}
										>
											{isFirstRow && (
												<Box
													component="td"
													rowSpan={shiftRowTypes.length}
													sx={{
														p: 1.5,
														fontWeight: 700,
														color: isShiftHovered ? BLUE[800] : SLATE[800],
														textAlign: "center",
														bgcolor: isShiftHovered ? BLUE[100] : SLATE[50],
														borderRight: `1px solid ${SLATE[100]}`,
														transition: "background 150ms ease",
													}}
												>
													<Tooltip title={label} placement="top" arrow>
														<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 14, lineHeight: 1, whiteSpace: "nowrap" }}>
															<ShiftTypeIcon shiftType={shiftLabelToShiftType(label)} />
															<Box component="span">{label}</Box>
														</Box>
													</Tooltip>
												</Box>
											)}
											<Box
												component="td"
												sx={{
													p: 1.5,
													fontSize: 10,
													fontWeight: isMissingRow ? 800 : 600,
													color: rowLabelColor(rowType, Boolean(isRowHovered)),
													textTransform: "uppercase",
													letterSpacing: "0.04em",
													transition: "background 150ms ease",
													// Missing is the table's headline result — anchor it with a left accent + tint.
													...(isMissingRow
														? { borderLeft: `4px solid ${BLUE[600]}`, bgcolor: isRowHovered ? BLUE[100] : BLUE[50], cursor: "help" }
														: { bgcolor: isRowHovered ? BLUE[50] : "transparent" }),
												}}
											>
												{isMissingRow ? (
													<Tooltip title={t("hiring.gaps.missingTooltip")} placement="top" arrow>
														<Box component="span">{rowLabel(rowType)}</Box>
													</Tooltip>
												) : (
													rowLabel(rowType)
												)}
											</Box>
											{renderMetricCells(label, rowType)}
										</Box>
									);
								});
							})}
							{detailedView && Object.keys(templateHppd).length > 0 && (
								<Box component="tr" data-testid="hiring-row-template-hppd" sx={{ borderTop: `2px solid ${SLATE[200]}` }}>
									<Tooltip
										title={t(
											includeFlags.internal_pickup_ot || includeFlags.internal_pickup_non_ot
												? "hiring.gaps.templateHppdTrendTooltip"
												: "hiring.gaps.templateHppdTooltip",
										)}
										placement="top"
										arrow
									>
										<Box
											component="td"
											colSpan={2}
											sx={{
												p: 1.5,
												fontSize: 10,
												fontWeight: 700,
												color: SLATE[600],
												textAlign: "center",
												textTransform: "uppercase",
												letterSpacing: "0.04em",
												bgcolor: SLATE[50],
												cursor: "help",
											}}
										>
											{t("hiring.gaps.templateHppd")}
										</Box>
									</Tooltip>
									{weekIndices.flatMap((weekIdx) =>
										columns.map((column) => {
											const dayIdx = columnToDayIdx(column, weekStartDay);
											const dayKey = dayKeyAt(column, weekStartDay);
											const value = templateHppd[dayIdx];
											return (
												<Box
													component="td"
													key={`hppd-${weekIdx}-${dayKey}`}
													data-testid={`hiring-cell-template-hppd-${weekIdx}-${dayIdx}`}
													sx={{ p: 1.5, textAlign: "center", fontWeight: 700, color: SLATE[600], borderLeft: `1px solid ${SLATE[50]}` }}
												>
													{value !== undefined ? value.toFixed(2) : "–"}
												</Box>
											);
										}),
									)}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
				<Typography sx={{ display: "block", px: 2.5, py: 1.5, fontSize: 11, color: SLATE[400], borderTop: `1px solid ${SLATE[100]}` }}>
					{/* ONE formatter with the headers above it: this line used to render `9/21/2025 – 9/27/2025`
					    directly under a header reading `21–27 Sep`, which looked like two different windows.
					    Earliest→latest rather than first→last entry: the week columns are in cycle order, so
					    reading the ends of the array prints the span backwards whenever week 1 is the later week. */}
					{t("hiring.gaps.legend", { range: formatWeekRangeWithMonth(...displayedDateSpan(dates)) })}
				</Typography>
			</Collapse>
		</Box>
	);
};

export default RecurringGapsTable;
